/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import error from '../../../../common/nodejs/errors';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  EXECUTION: 'execution',
  SUCCESS: 'success',
  FAILED: 'failed',
  ERROR: 'error'
};

/** 現在セッション */
const SESSION_PROPERTY = {
  DEFAULT: 0,
  NON_DEFAULT: 1
};

export default {
  name: 'requirement149',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      sessionProperty: SESSION_PROPERTY.DEFAULT,
      modal_requirement_149: false,
      isExecuteSuccess: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.ERROR:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.EXECUTION:
        case PHASE.START:
        default:
          return 'secondary';
      }
    },

    /**
     * ダイアログのヘッダー文言
     */
    headerTitle() {
      // 待機
      const startTitle = 'message.common_title_confirmation';
      // 待機, 実行中
      const ExecutionTitle = 'individual.common_title_execution';
      // 正常終了
      const SuccessTitle = 'message.common_title_success';
      // 中止失敗, 共通エラー
      const ErrorTitle = 'message.common_title_error';
      const titleMap = {
        start: startTitle,
        execution: ExecutionTitle,
        success: SuccessTitle,
        error: ErrorTitle,
        failed: ErrorTitle
      };
      return this.$t(titleMap[this.phase]);
    },

    /** 表示メッセージ */
    workSupportMessage() {
      const messageMap = {
        start: `${this.i18nWsKey}.REQ_WS_149_TXT_1`,
        success: `${this.i18nWsKey}.REQ_WS_149_TXT_2`,
        failed: `${this.i18nWsKey}.REQ_WS_149_TXT_3`,
        error: `individual.error_communication_error`
      };
      return this.$t(messageMap[this.phase]);
    },

    // 「OK」ボタンの表示確認
    showButtonOk() {
      const showOkButtonMap = {
        start: true,
        success: true,
        failed: true,
        error: true
      };
      let result = showOkButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    // 「キャンセル」ボタンの表示判定
    showButtonCancel() {
      const showCancelButtonMap = {
        start: true,
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.sessionProperty = SESSION_PROPERTY.DEFAULT;
      this.modal_requirement_149 = true;
      this.isExecuteSuccess = false;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_149 = false;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** 「OK」ボタンの押下処理 */
    onOkClicked() {
      switch (this.phase) {
        case 'start':
          this.onConfirmOkClicked();
          break;
        case 'success':
        case 'failed':
          this.exit();
          break;
        case 'error':
          this.onErrorOkClicked();
          break;
        default:
          break;
      }
    },

    /** 「キャンセル」ボタンの押下処理 */
    onCancelClicked() {
      this.closeWizard();
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.ERROR;
    },

    /** 待機画面「OK」押下 */
    async onConfirmOkClicked() {
      this.isLoading = true;
      // 実行画面へ遷移
      this.phase = PHASE.EXECUTION;
      this.standby();
    },

    /** 待機処理 */
    async standby() {
      try {
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();

        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'standby'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // 正常コールバック
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              // NonDefaultSession遷移が実行済み
              this.sessionProperty = SESSION_PROPERTY.NON_DEFAULT;
              await this.execute();
              break;
            case REQUEST_STATUS_FAILED:
              // デバイス断のエラー検知 フェールセーフ
              if (!response.data.user_data) {
                this.sessionProperty = SESSION_PROPERTY.NON_DEFAULT;
                // 共通エラー画面へ遷移
                this.showUnexpectedError();
                break;
              }
              // 異常終了：
              // NonDefaultSession遷移が実行済み
              if (response.data.user_data.is_session) {
                this.sessionProperty = SESSION_PROPERTY.NON_DEFAULT;
              }
              // 共通エラー画面へ遷移
              this.showUnexpectedError();
              break;
            default:
              // 異常終了：共通エラー
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 実行開始処理 (実行,実行監視) */
    async execute() {
      try {
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();

        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // 正常コールバック
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              // 正常終了
              this.isExecuteSuccess = true;
              // 停止処理
              await this.stop();
              break;
            case REQUEST_STATUS_FAILED:
              // 異常終了
              switch (response.data.async_code) {
                case error.WSP_149_EXECUTE_ERROR:
                  // terminate処理
                  await this.terminate();
                  break;
                case error.WSP_149_CHECK_PROCESS_ERROR:
                  // 停止処理
                  await this.stop();
                  break;
                default:
                  this.showUnexpectedError();
              }
              break;
            default:
              // 異常終了：共通エラー
              this.showUnexpectedError();
              return;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** ルーチン停止処理 */
    async stop() {
      const systemId = individualDiagnose.getCurrentSystemId(this);
      const token = session.getToken();

      const response = await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        systemId,
        this.workSupportId,
        {
          id_token: token.id_token,
          type: 'stop'
        },
        // エラー発生のコールバックを実装
        // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
      );

      if (response) {
        // 実行結果にかかわらずterminate処理に進む
        await this.terminate();
      }

    },

    /** terminate処理 */
    async terminate() {
      try {
        this.isLoading = true;
        // 実行後の処理分岐のために実行時のフェーズを保持
        const currentPhase = this.phase;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();

        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'terminate'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (currentPhase === PHASE.ERROR) {
          // 実行時にエラー画面だった場合は機能終了
          await this.exit();
          return;
        }

        if (response) {
          // 実行結果にかかわらず次の処理に進む
          if (this.isExecuteSuccess) {
            // 停止画面へ遷移
            this.phase = PHASE.SUCCESS;
          } else {
            // 中止失敗画面へ遷移
            this.phase = PHASE.FAILED;
          }
        }

      } finally {
        this.isLoading = false;
      }
    },

    /** dispose処理 */
    async dispose() {
      try {
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();

        return await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'dispose'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列、もしくは独自定義した文字列が返ってくる。
          // 必要に応じて、errorType に応じてメッセージを変えるといったことも可能。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

      } finally {
        this.isLoading = false;
      }
    },

    /** 共通エラー画面 OK押下処理 */
    async onErrorOkClicked() {
      // 現在セッションがNonDefaultSessionの場合はterminateを実行する
      if (this.sessionProperty === SESSION_PROPERTY.NON_DEFAULT) {
        await this.terminate();
      } else {
        await this.exit();
      }
    },

    /** 機能終了処理 */
    async exit() {
      await this.dispose();
      this.closeWizard();
    },
  },

  /**
   * DOM作成後実行処理
   */
  async mounted() {
    this.init();
  },
};